import { navigate, PageProps } from 'gatsby';
import { ReactNode, useEffect } from 'react';
import { Layout } from 'components/layout/Layout';
import { PublicLayout } from 'components/layout/PublicLayout';
import { ResultsLayout } from 'components/layout/ResultsLayout';
import { LoadingSpinner } from 'components/LoadingSpinner';
import { ServerError } from 'components/errorPages/ServerError';
import { AccessError } from 'components/errorPages/AccessError';
import { RoleAccessDenied } from 'components/errorPages/RoleAccessDenied';
import { NotFound } from 'components/errorPages/NotFound';
import { useTheme } from 'hooks/useTheme';
import { useUser } from 'services/User';
import { useUserData } from 'services/api/UserData';
import { canAccessPath, shouldRedirectToDashboard } from 'services/accessControl';
import { DashboardTabs } from 'services/tabs';

import '@opswat/ui/styles';
import '../../tailwind.css';
import 'components/WrapPage.css';

const PUBLIC_ROUTES = ['/', '/extension/get-apikey', '/product'];
const GUEST_PUBLIC_ROUTE_PREFIXES = ['/integrations'];
const AUTH_OPTIONAL_ROUTES = ['/licensing'];

const HIDE_AS_NOT_FOUND_PREFIXES = ['/store'];

type WrapPageProps = Omit<PageProps, 'children'> & { children?: ReactNode; };

export const WrapPage = ({ children, ...props }: WrapPageProps) => {
    const pathname = props.location.pathname;
    const isPublic = PUBLIC_ROUTES.includes(pathname);
    const isAuthOptional = AUTH_OPTIONAL_ROUTES.includes(pathname);
    const { user } = useUser();
    const { error, isLoading } = useUserData({
        onSuccess: () => {
            if (pathname.startsWith('/extension/get-apikey') && window) {
                window.close();
            }
        },
    });

    useTheme();

    // Skip the home->dashboard redirect when the user explicitly clicked the Home button.
    const cameFromHomeButton = Boolean((props.location.state as { fromHomeButton?: boolean; } | null)?.fromHomeButton);
    const redirectHomeToDashboard = pathname === '/' && !cameFromHomeButton && shouldRedirectToDashboard(user);
    useEffect(() => {
        if (redirectHomeToDashboard) {
            navigate(`/dashboard/${DashboardTabs.Security}`);
        }
    }, [redirectHomeToDashboard]);

    if (isLoading || redirectHomeToDashboard) {
        return <LoadingSpinner />;
    }

    if (isPublic || (isAuthOptional && user?.isLoggedIn !== true)) {
        return (
            <PublicLayout {...props}>
                {children}
            </PublicLayout>
        );
    }

    if (error?.response?.data?.code === 403004) {
        return (
            <PublicLayout {...props}>
                <AccessError />
            </PublicLayout>
        );
    }

    if (error?.response?.data?.code === 403002) {
        return (
            <PublicLayout {...props}>
                <AccessError link={false} />
            </PublicLayout>
        );
    }
    if (error) {
        return (
            <PublicLayout {...props}>
                <ServerError />
            </PublicLayout>
        );
    }

    const matchesHideAsNotFound = HIDE_AS_NOT_FOUND_PREFIXES.some((prefix) => pathname.startsWith(prefix));

    if (matchesHideAsNotFound && !canAccessPath(user, pathname)) {
        return (
            <PublicLayout {...props}>
                <NotFound />
            </PublicLayout>
        );
    }

    if (user && !canAccessPath(user, pathname)) {
        return (
            <PublicLayout {...props}>
                <RoleAccessDenied />
            </PublicLayout>
        );
    }

    if (pathname.startsWith('/results')) {
        return <ResultsLayout {...props}>{children}</ResultsLayout>;
    }

    if (pathname.startsWith('/blog')) {
        return <PublicLayout {...props}>{children}</PublicLayout>;
    }

    const isGuestPublicRoute = GUEST_PUBLIC_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix));
    if (isGuestPublicRoute && !user?.isLoggedIn) {
        return <PublicLayout {...props}>{children}</PublicLayout>;
    }

    return <Layout {...props}>{children}</Layout>;
};
